import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Delta, the up or down movement next to a number.
 *
 * Source selectors: `system.css` `.dsk-delta-up`, `.dsk-delta-down`. Note the
 * single hyphen: these are two sibling classes upstream, not `--` modifiers of
 * a `.dsk-delta` block, and there is no unmodified base class to fall back to.
 * A delta with no direction therefore renders untinted, which is the honest
 * result for a movement of zero.
 *
 * Doctrine enforced:
 * - Colour is `--success` / `--error`, and it is never the ONLY carrier of the
 *   meaning: pass a sign or an arrow glyph in the children so the direction
 *   survives a colour-blind reader and a greyscale print.
 * - Direction is derived from `value` when you pass a number, so the sign and
 *   the tint cannot drift apart the way they do when a caller sets both.
 */
export const Delta = React.forwardRef(function Delta(
  { direction, value, as: Component = 'span', className, ...rest },
  ref,
) {
  let dir = direction;
  if (!dir && typeof value === 'number') {
    dir = value > 0 ? 'up' : value < 0 ? 'down' : undefined;
  }

  const dirClass = { up: 'dsk-delta-up', down: 'dsk-delta-down' }[dir] || null;

  return <Component ref={ref} className={cn(dirClass, className)} {...rest} />;
});
Delta.displayName = 'Delta';

export default Delta;
